import React, { memo, useMemo, useEffect, useRef, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  Endpoints,
  GenericError,
  UrlPath,
  UserId,
  Verbiage,
  loaderStyle,
  maxPageSize,
  ProductDeliveryPlatforms,
  ProductTypes,
  AutoSelectPkgDist,
  Cancelled,
  Rejected,
  PendingInactive,
  PendingActive,
  positiveStatuses,
  Blocked,
  Active,
  PendingApproval,
  Draft,
} from '../../constants';
import smartHttp from '../../helpers/smartHttp';
import AgGridComponent from '../../sharedcomponents/ag-grid/AgGrid';
import { Notify } from './../../sharedcomponents/Alert/Notify';
import DistributorUserIdModal from './DistributorUserIdModal';
import DistributorsDropdown from './DistributorsDropdown';
import PackagesDropdown from './PackagesDropdown';
import UsersDropdown from './UsersDropdown';
import Loader from './../../components/Loader';
import InfoIcon from './../../assets/icons/common/info.png';
import StickyTooltip from './../../sharedcomponents/StickyTooltip/StickyTooltip';
import store from '../../redux/store';
import StatusDefinitionHeader, {
  getStatusDefinition,
} from './StatusDefinitionHeader';
import { getSelectedContractNumbers } from '../../helpers/component-helper-methods';

const AddEntitlements = memo(
  ({ contracts, isBPMReferral }) => {
    const ManageUserIdDistributors =
      store.getState()?.dbConfig.ManageUserIdDistributors;
    const TradingEntityDistributorPlatforms = ManageUserIdDistributors?.filter(
      (item) => item.tradingEntity === 1,
    );

    const DistributorUserIDRequiredList = store
      .getState()
      ?.dbConfig.ManageUserIdDistributors?.filter(
        (item) => item.isRequired === 1,
      );

    const getColumnClass = (params) => {
      if (params?.node?.data?.isEntitlementExists == false) {
        return 'new-selected-entitlement-row grid-cell-left';
      } else {
        return 'grid-cell-left';
      }
    };

    const columns = [
      {
        headerName: 'User',
        headerTooltip: 'User',
        field: 'contactData',
        tooltipField: 'contactData',
        initialFlex: 3,
        initialHide: false,
        cellClass: getColumnClass,
      },
      {
        headerName: 'Package',
        headerTooltip: 'Package',
        field: 'packageName',
        tooltipField: 'packageName',
        initialFlex: 3,
        initialHide: false,
        cellClass: getColumnClass,
      },
      {
        headerName: 'Delivery Platform (Type)',
        headerTooltip: 'Delivery Platform (Type)',
        field: 'distributorData',
        tooltipField: 'distributorData',
        initialFlex: 2,
        initialHide: false,
        cellClass: getColumnClass,
      },
      {
        headerName: 'Status',
        headerTooltip: 'Status',
        field: 'status',
        tooltipValueGetter: (params) => getStatusDefinition(params.value),
        headerComponent: StatusDefinitionHeader,
        initialFlex: 2,
        initialHide: false,
        cellClass: getColumnClass,
        cellRenderer: (params) => {
          const isTooltipVisible =
            tooltip.showTooltip && tooltip.rowId === params.rowIndex;

          return (
            <div style={{ position: 'relative' }} data-row-id={params.rowIndex}>
              <span>
                {params.value}
                {params.value === 'Blocked' && (
                  <img
                    src={InfoIcon}
                    className="info-icon"
                    style={{
                      cursor: 'pointer',
                      marginLeft: '5px',
                      width: '20px',
                    }}
                    title={params.data.blockedReason}
                    onClick={(event) => {
                      handleTooltipClick(
                        event,
                        params.rowIndex,
                        params.data.blockedReason,
                      );
                    }}
                  />
                )}
                {isTooltipVisible && (
                  <StickyTooltip
                    ref={tooltipRef}
                    text={tooltip.text}
                    onClose={() => setTooltip({})}
                  />
                )}
              </span>
            </div>
          );
        },
      },
    ];
    const gridRef = useRef();
    const onDragStoppedHandler = (params) => {
      const columnDefs = columns;
      let groupCols = params.api.columnModel.rowGroupColumns;
      columnDefs.forEach(function (colDef, index) {
        if (groupCols.find((x) => x.colId === colDef.field)) {
          colDef.hide = true;
        } else {
          colDef.hide = false;
        }
      });
      gridRef.current.api.setGridOption('columnDefs', columnDefs);
    };

    const handleTooltipClick = (event, rowId, blockedReason) => {
      event.stopPropagation();
      if (tooltip.rowId === rowId) {
        setTooltip({});
      } else {
        setTooltip({
          showTooltip: true,
          rowId: rowId,
          text: blockedReason,
        });
      }
    };

    const defaultConfig = {
      rowModelType: 'clientSide',
      isExportCSV: false,
      isExportExcel: false,
      pivotPanelShow: '',
      pagination: false,
      isAutoSizeColums: true,
      enableCharts: false,
      sideBar: false,
      onDragStopped: onDragStoppedHandler,
    };

    const defaultColdef = {
      sortable: true,
      resizable: true,
      menuTabs: ['generalMenuTab', 'filterMenuTab'],
      enableValue: false,
    };

    const autoGroupColumnDef = {
      // column definition for grouped column
      enableRowGroup: false,
      lockPosition: 'left',
      lockPinned: true,
      sortable: true,
      cellRenderer: 'agGroupCellRenderer',
    };

    const [loading, setLoading] = useState(false);
    const [packageDataSet, setPackageDataSet] = useState();
    const [distributorDataSet, setDistributorDataSet] = useState();
    const [selectedPackages, setSelectedPackages] = useState([]);
    const [selectedDistributors, setSelectedDistributors] = useState([]);
    const [selectedDistributorsId, setSelectedDistributorsId] = useState([]);
    const [selectedParentCascade, setSelectedParentCascade] = useState();
    const [addPlattsConnect, setAddPlattsConnect] = useState(false);
    const [filteredPackages, setFilteredPackages] = useState();
    const [filteredDistributors, setFilteredDistributors] = useState();
    const [userEntitlementsData, setUserEntitlementsData] = useState([]);
    const [entitlementData, setEntitlementData] = useState([]); // Holds the list of current entitlements for the selected contracts
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [updatedUsers, setUpdatedUsers] = useState([]);
    const [packageDistributorData, setPackageDistributorData] = useState([]);
    const [updatedPackageDistributors, setUpdatedPackageDistributors] =
      useState([]);
    const [showDistributorUserIdModal, setShowDistributorUserIdModal] =
      useState([]);
    const [tooltip, setTooltip] = useState({});
    const tooltipRef = useRef(null);
    const gridContainerRef = useRef(null);
    const userExpansionRequestRef = useRef(0);

    useEffect(() => {
      const reduceCallback = setTimeout(() => {
        setPackageDistributorData([]);
        setFilteredPackages();
        setSelectedPackages([]);
        setSelectedDistributors([]);
        setSelectedDistributorsId([]);
        setSelectedUsers([]);
        getAllUserEntitlements();
      }, 1000);
      return () => clearTimeout(reduceCallback);
    }, [contracts]);

    useEffect(() => {
      // Set initial state
      if (packageDataSet) setFilteredPackages(packageDataSet);
      if (distributorDataSet) setFilteredDistributors(distributorDataSet);
    }, [packageDataSet, distributorDataSet]);

    useEffect(() => {
      // Controller to abort previous API calls if any ongoing flight
      const controller = new AbortController();
      const signal = controller.signal;
      const reduceCallback = setTimeout(() => {
        if (selectedUsers.length > 0 && selectedPackages.length > 0) {
          if (isAnyAutoSelectPkg()) {
            getContractPackages(
              signal,
              false,
              AutoSelectPkgDist.Market_Basics.distId,
            );
            return;
          }
        }
        if (selectedPackages.length > 0 && selectedDistributors.length > 0) {
          getContractPackages(signal, false);
        } else if (
          selectedPackages.length === 0 ||
          selectedDistributors.length === 0
        ) {
          // revert any recycled rows
          setPackageDistributorData(
            restoreExistingRows(packageDistributorData),
          );
        }
      }, 1000);
      setTooltip({});
      return () => {
        clearTimeout(reduceCallback);
        controller?.abort();
      };
    }, [selectedPackages, selectedDistributors, selectedUsers]);

    useEffect(() => {
      if (selectedParentCascade === 'PackageDropdown' && userEntitlementsData) {
        // Filter distributor based on package selection
        let pkgId = [];
        selectedPackages.forEach((el) => {
          pkgId.push(el.packageName);
        });
        let filterData = userEntitlementsData.filter((el) =>
          pkgId.includes(el.packageName),
        );
        // Filter distributors based on selected packages and indicate if user already has entitlement for that distributor
        const entitlementDistributorIds = new Set(
          entitlementData.map(({ distributorId }) => distributorId),
        );
        const distributorResults = filterData.map((platform) => ({
          ...platform,
          indicatePlatform: entitlementDistributorIds.has(
            platform.distributorId,
          ),
        }));
        setFilteredDistributors({ results: distributorResults });
      }
    }, [selectedPackages]);

    useEffect(() => {
      if (
        selectedParentCascade === 'DistributorDropdown' &&
        userEntitlementsData
      ) {
        // Filter packages based on distributor selection
        let filterData = userEntitlementsData.filter((el) => {
          return selectedDistributorsId.includes(el.distributorId);
        });
        setFilteredPackages({ results: filterData });
      }
    }, [selectedDistributorsId]);

    useEffect(() => {
      handleUserCallback(updatedUsers);
    }, [updatedUsers]);

    const expandUsersAcrossSelectedContracts = async (users = []) => {
      try {
        if (!users?.length) return [];
        const { contractNumbers, contractNumberFilter } =
          getSelectedContractNumbers(contracts || []);
        if (!contractNumbers?.length) return users;

        const uniqueContactIds = [
          ...new Set(users.map((u) => u.contactId).filter(Boolean)),
        ];
        if (!uniqueContactIds.length) return users;

        const queryParamsUser = {
          PageSize: maxPageSize,
          id: String(Date.now()),
          Field: 'contactName,contactId,email,contractNumber,groupId',
          Filter: `contractNumber in(${contractNumberFilter}) and contactId in(${uniqueContactIds
            .map((id) => `"${id}"`)
            .join(',')})`,
          Sort: 'contactName:asc',
        };

        const usersResponse = await smartHttp.get(
          Endpoints.userApi,
          queryParamsUser,
        );
        const apiUsers = (usersResponse?.data?.results || []).filter(
          (x) => !x.groupId,
        );

        const expandedUsers = apiUsers.map((u) => ({
          contactName: `${u.contactName}  (${u.email})`,
          emailId: u.email,
          contactId: u.contactId,
          contractNumber: u.contractNumber,
        }));

        if (expandedUsers.length) return expandedUsers;
        return users;
      } catch (error) {
        return users;
      }
    };

    useEffect(() => {
      if (tooltip.showTooltip && gridContainerRef.current) {
        const scrollContainer =
          gridContainerRef.current.querySelector('.ag-body-viewport');
        const handleScroll = () => {
          const rowElement = document.querySelector(
            `[data-row-id="${tooltip.rowId}"]`,
          );
          if (rowElement) {
            const gridRect = scrollContainer.getBoundingClientRect();
            const rowRect = rowElement.getBoundingClientRect();
            if (
              rowRect.bottom < gridRect.top ||
              rowRect.top > gridRect.bottom
            ) {
              setTooltip({});
            }
          } else {
            setTooltip({});
          }
        };
        if (scrollContainer) {
          scrollContainer.addEventListener('scroll', handleScroll);
        }
        return () => {
          if (scrollContainer) {
            scrollContainer.removeEventListener('scroll', handleScroll);
          }
        };
      }
    }, [tooltip.showTooltip, tooltip.rowId]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          tooltipRef.current &&
          !tooltipRef.current.contains(event.target) &&
          !event.target.classList.contains('info-icon')
        ) {
          setTooltip({});
        }
      };

      if (tooltip.showTooltip) {
        document.addEventListener('click', handleClickOutside);
      } else {
        document.removeEventListener('click', handleClickOutside);
      }

      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }, [tooltip.showTooltip]);

    const isAnyAutoSelectPkg = () => {
      try {
        let isAutoSelect = false;
        const marketBasicsSelected = selectedPackages.some((pkg) =>
          pkg.packageName.includes(AutoSelectPkgDist.Market_Basics.pkgName),
        );

        // Check if Market Basics is selected
        if (marketBasicsSelected) {
          const plattsConnectSelected = selectedDistributors.find(
            (dst) =>
              dst.distributorId == AutoSelectPkgDist.Market_Basics.distId,
          );
          // Platts Connect is not selected
          if (!plattsConnectSelected) {
            isAutoSelect = true;
          }
        }
        return isAutoSelect;
      } catch (error) {}
    };

    // reset mutated rows with negative status back to their original state
    const restoreExistingRows = (rows = []) =>
      rows
        .map((r) =>
          !r.isEntitlementExists && r.originalStatus
            ? { ...r, status: r.originalStatus, isEntitlementExists: true }
            : r,
        )
        .filter((r) => r.isEntitlementExists);

    const getContractPackages = async (
      signal,
      isUserChanged = false,
      autoSelectedDistributor = '',
    ) => {
      try {
        let packageDistributorResponse;

        if (!isUserChanged) {
          const packageIdCollection = selectedPackages
            .map((el) => String(el.packageId))
            .join(',');

          let distributorIdCollection = selectedDistributors
            .map((el) => String(el.distributorId))
            .join(',');
          if (autoSelectedDistributor) {
            distributorIdCollection = distributorIdCollection
              ? `${distributorIdCollection},${autoSelectedDistributor}`
              : autoSelectedDistributor;
          }
          if (packageIdCollection && distributorIdCollection) {
            const { contractNumbers, contractNumberFilter } =
              getSelectedContractNumbers(contracts);
            let queryParamsUser = {
              PageSize: maxPageSize,
              Filter: `packageId in(${packageIdCollection}) and distributorId in(${distributorIdCollection}) and contractNumber in(${contractNumberFilter})`,
            };
            setLoading(true);
            packageDistributorResponse = await smartHttp.get(
              Endpoints.contractPackagesApi,
              queryParamsUser,
              { axiosConfig: { signal } },
            );
            setLoading(false);
            setUpdatedPackageDistributors(packageDistributorResponse);
          }
        } else {
          packageDistributorResponse = updatedPackageDistributors;
        }
        if (
          selectedPackages.length !== 0 &&
          (selectedDistributors.length !== 0 || autoSelectedDistributor) &&
          selectedUsers.length !== 0
        ) {
          let preparePackageDistributorData = restoreExistingRows(
            packageDistributorData,
          );

          preparePackageDistributorData.forEach((x) => {
            if (!x.isEntitlementExists) {
              x.isEntitlementExists =
                !packageDistributorResponse?.data?.results?.some(
                  (k) =>
                    k.contractNumber === x.contractNumber &&
                    k.productComponentId === x.productComponentId,
                );
            }
          });

          selectedUsers.forEach((u) => {
            packageDistributorResponse?.data?.results?.forEach((pkgPlat) => {
              if (pkgPlat.contractNumber !== u.contractNumber) return;

              const existingIdx = preparePackageDistributorData.findIndex(
                (k) =>
                  k.contactId === u.contactId &&
                  k.contractNumber === u.contractNumber &&
                  k.productComponentId === pkgPlat.productComponentId,
              );
              const existingRow =
                existingIdx > -1
                  ? preparePackageDistributorData[existingIdx]
                  : null;

              if (
                existingRow &&
                [Cancelled, Rejected, PendingInactive].includes(
                  existingRow.status,
                )
              ) {
                const recycled = {
                  ...existingRow,
                  originalStatus:
                    existingRow.originalStatus ?? existingRow.status,
                  status: existingRow.status,
                  isEntitlementExists: false,
                };
                preparePackageDistributorData.splice(existingIdx, 1);
                preparePackageDistributorData.unshift(recycled);
                return;
              }

              if (
                !preparePackageDistributorData.some(
                  (pd) =>
                    pd.contactId === u.contactId &&
                    pd.contractNumber === u.contractNumber &&
                    pd.productComponentId === pkgPlat.productComponentId &&
                    (!pd.isEntitlementExists ||
                      [
                        Active,
                        PendingActive,
                        PendingApproval,
                        Blocked,
                        Draft,
                      ].some((status) => pd.status === status)),
                )
              ) {
                pkgPlat.distributorData =
                  pkgPlat.distributor +
                  ' : ' +
                  pkgPlat.distributorPlatform +
                  ' (' +
                  pkgPlat.deliveryType +
                  ')';
                pkgPlat.contactData = u.contactName;
                pkgPlat.status = '';
                pkgPlat.contactId = u.contactId;
                pkgPlat.isEntitlementExists = false;
                preparePackageDistributorData.unshift({ ...pkgPlat });
              }
            });
          });
          if (
            preparePackageDistributorData.length > 0 &&
            selectedPackages.some((pkg) =>
              pkg.packageName.includes(AutoSelectPkgDist.Market_Basics.pkgName),
            )
          ) {
            preparePackageDistributorData =
              preparePackageDistributorData.filter(
                (el) =>
                  el.packageName.includes(
                    AutoSelectPkgDist.Market_Basics.pkgName,
                  ) ||
                  selectedDistributorsId.some((id) => id == el.distributorId) ||
                  el.isEntitlementExists,
              );
          }
          setPackageDistributorData(preparePackageDistributorData);
        }
      } catch (err) {
        setLoading(false);
      }
    };

    const getAllUserEntitlements = async () => {
      try {
        if (!contracts?.length || !contracts.some((el) => el.checked)) {
          setUserEntitlementsData([]);
          setPackageDataSet([]);
          setDistributorDataSet([]);
          return;
        }
        const { contractNumbers, contractNumberFilter } =
          getSelectedContractNumbers(contracts);
        if (!contractNumbers.length) {
          setUserEntitlementsData([]);
          setPackageDataSet([]);
          setDistributorDataSet([]);
          return;
        }
        const packageFields =
          'packageId,packageName,componentCount,distributor,distributorDisplayName,distributorPlatformDisplayName,distributorId,distributorPlatform,deliveryType,productComponentId,platformCategories';

        setLoading(true);
        let productTypes = ProductTypes?.map((t) => '"' + t + '"').join(',');
        const filteredDistributors = ProductDeliveryPlatforms.map(
          (s) => `"${s}"`,
        ).join(',');
        const [packageRes, entitlementRes] = await Promise.all([
          smartHttp.get(Endpoints.contractPackagesApi, {
            pageSize: maxPageSize,
            id: Date.now(),
            filter: `contractNumber in (${contractNumberFilter}) and distributor not in (${filteredDistributors})`,
            Field: packageFields,
          }),
          smartHttp.get(Endpoints.userentitlementsApi, {
            pageSize: maxPageSize,
            id: Date.now(),
            Field:
              'distributor,distributorId,distributorPlatform,distributorDisplayName,distributorPlatformDisplayName,deliveryType,producttype',
            filter: `contractNumber in (${contractNumberFilter}) AND status IN (${positiveStatuses
              .map((s) => `"${s}"`)
              .join(',')}) and distributor not in (${filteredDistributors})${
              productTypes ? ` and producttype not in (${productTypes})` : ''
            }`,
          }),
        ]);
        // Save entitlement data for later use (e.g., for distributor filtering)
        const filteredEntitlementData = entitlementRes?.data?.results || [];
        setEntitlementData(filteredEntitlementData);
        const filteredContractPackages = packageRes?.data?.results || [];
        // Distributor data
        const hcAccountId = contracts?.find((c) => c.checked)?.hC_AccountID;
        let mappedIdSet = new Set();
        if (hcAccountId) {
          try {
            const mappingRes = await smartHttp.getRapid(
              Endpoints.companyDistributorMappingApi,
              {
                pageSize: 5000,
                filter: `hcaccountid:"${hcAccountId}" AND isdeleted:0`,
              },
            );
            const mappedIds = (mappingRes?.data?.results || [])
              .map((m) => m.distributorId ?? m.distributorid)
              .filter((id) => id !== undefined && id !== null);
            mappedIdSet = new Set(mappedIds);
          } catch {
            /* ignore mapping errors, show all distributors */
          }
        }
        const filteredContractPackagesByMapping =
          mappedIdSet.size > 0
            ? filteredContractPackages.filter((p) =>
                mappedIdSet.has(p.distributorId),
              )
            : filteredContractPackages;
        setUserEntitlementsData(filteredContractPackagesByMapping);

        // Package data
        const packageData = [
          ...new Map(
            filteredContractPackagesByMapping
              .map(
                ({
                  distributorId,
                  packageId,
                  packageName,
                  productComponentId,
                  contractNumber,
                  componentCount,
                }) => ({
                  distributorId,
                  packageId,
                  packageName,
                  productComponentId,
                  contractNumber,
                  componentCount,
                }),
              )
              .map((item) => [item.packageId, item]),
          ).values(),
        ];

        const metadata = {
          ...(packageRes?.data?.metadata || {}),
          count: packageData.length,
        };
        setPackageDataSet({ metadata, results: packageData });

        const userEntitlements = entitlementRes?.data?.results || [];
        const entitlementDistributorIds = new Set(
          userEntitlements.map(({ distributorId }) => distributorId),
        );
        const distributorResults = filteredContractPackages
          .map((platform) => ({
            ...platform,
            indicatePlatform: entitlementDistributorIds.has(
              platform.distributorId,
            ),
          }))
          .filter(
            (platform) =>
              mappedIdSet.size === 0 || mappedIdSet.has(platform.distributorId),
          );
        const distributorMetadata = {
          ...(packageRes?.data?.metadata || {}),
          count: distributorResults.length,
        };
        setDistributorDataSet({
          metadata: distributorMetadata,
          results: distributorResults,
        });
      } catch (err) {
        setUserEntitlementsData([]);
        setPackageDataSet([]);
        setDistributorDataSet([]);
      } finally {
        setLoading(false);
      }
    };

    const cascadingPackageDistributor = () => {
      // On empty selection, set to default data
      if (
        selectedPackages.length === 0 &&
        selectedParentCascade === 'PackageDropdown'
      ) {
        setFilteredDistributors(distributorDataSet);
      } else if (
        selectedDistributorsId.length === 0 &&
        selectedParentCascade === 'DistributorDropdown'
      ) {
        setFilteredPackages(packageDataSet);
      }

      // Cascading functionality to set which dropdown is parent
      if (selectedPackages.length === 0 && selectedDistributors.length === 0) {
        setSelectedParentCascade();
      } else if (!selectedParentCascade) {
        if (selectedPackages.length > 0) {
          setSelectedParentCascade('PackageDropdown');
        }
        if (selectedDistributors.length > 0) {
          setSelectedParentCascade('DistributorDropdown');
        }
      }
    };

    // Memoized selection to stop loop from dropdown callback
    const cascadingPackageDistributorMemo = useMemo(
      cascadingPackageDistributor,
      [selectedPackages, selectedDistributors],
    );
    // get all entitlement mappings for user
    const getEntitlementMappings = (userIds, props) => {
      if (contracts && contracts.some((x) => x.checked)) {
        const { contractNumbers, contractNumberFilter } =
          getSelectedContractNumbers(contracts);
        let productTypes = ProductTypes?.map((t) => '"' + t + '"').join(',');
        let filteredDeliveryPlatforms = ProductDeliveryPlatforms.map(
          (s) => `"${s}"`,
        ).join(',');
        let queryParams = {
          pageSize: maxPageSize,
          Filter: `contractNumber in(${contractNumberFilter})
               AND contactid in(${userIds
                 ?.map((t) => (t.contactId ? '"' + t.contactId + '"' : ''))
                 .join(
                   ',',
                 )}) and distributor not in (${filteredDeliveryPlatforms}) and producttype not in (${productTypes})
          `,
          Sort: 'statusSortOrder:asc,modifiedDate:desc',
        };
        setLoading(true);
        smartHttp
          .get(Endpoints.userentitlementsApi, queryParams)
          .then((res) => {
            const response = res?.data?.results
              ?.filter((x) => !ProductDeliveryPlatforms.includes(x.distributor))
              .map((c, index) => {
                c.id = index;
                c.isEntitlementExists = true;
                c.distributorData =
                  c.distributor +
                  ' : ' +
                  c.distributorPlatform +
                  ' (' +
                  c.deliveryType +
                  ')';
                const matchedUser = userIds.find(
                  (x) =>
                    x.contactId == c.contactId &&
                    x.contractNumber == c.contractNumber,
                );
                c.contactData =
                  matchedUser?.contactName ||
                  userIds.find((x) => x.contactId == c.contactId)?.contactName;
                return c;
              });
            if (packageDistributorData.length > 0) {
              setPackageDistributorData([
                ...packageDistributorData,
                ...response,
              ]);
            } else {
              setPackageDistributorData(response);
            }
          })
          .catch((err) => {})
          .finally(() => {
            setLoading(false);
            if (props) setSelectedUsers(JSON.parse(JSON.stringify(props)));
          });
      } else {
        if (props) setSelectedUsers(JSON.parse(JSON.stringify(props)));
      }
    };

    const handleUserCallback = (props) => {
      //get users diff
      let removedUsers =
        props?.length > 0
          ? selectedUsers?.filter(
              (c) =>
                !props.some(
                  (pc) =>
                    pc.contactId == c.contactId &&
                    pc.contractNumber == c.contractNumber,
                ),
            )
          : selectedUsers;
      let addedUsers =
        selectedUsers?.length > 0
          ? props.filter(
              (c) =>
                !selectedUsers.some(
                  (pc) =>
                    pc.contactId == c.contactId &&
                    pc.contractNumber == c.contractNumber,
                ),
            )
          : props;

      // user added
      // add data to initials user entitlements
      if (addedUsers?.length > 0) {
        getEntitlementMappings(addedUsers, props);
      }
      // user removed
      // remove data from initial user entitlements
      if (removedUsers?.length > 0) {
        setPackageDistributorData(
          packageDistributorData.filter(
            (x) =>
              !removedUsers.some(
                (k) =>
                  k.contactId === x.contactId &&
                  k.contractNumber === x.contractNumber,
              ),
          ),
        );
        if (props) setSelectedUsers(JSON.parse(JSON.stringify(props)));
      }
    };
    const callBackUsers = async (props) => {
      const requestId = ++userExpansionRequestRef.current;
      const expandedUsers = await expandUsersAcrossSelectedContracts(props);
      if (requestId === userExpansionRequestRef.current) {
        setUpdatedUsers(expandedUsers);
      }
    };
    const callBackPackages = (props) => setSelectedPackages(props);
    const callBackDistributors = (params) => {
      let dstsId = [];
      params.forEach((dst) => {
        dstsId.push(dst.distributorId);
      });
      setSelectedDistributors(params);
      setSelectedDistributorsId(dstsId);
    };

    const onGridReadyHandler = (params) => {
      gridRef.current = params;
      params.api.showLoadingOverlay();
    };

    const onSaveHandler = () => {
      try {
        if (
          selectedPackages.length !== 0 &&
          (selectedDistributors.length !== 0 || isAnyAutoSelectPkg()) &&
          selectedUsers.length !== 0 &&
          packageDistributorData.length > 0
        ) {
          let entitleToSave = packageDistributorData.filter(
            (e) => !e.isEntitlementExists,
          );
          let requiredEntitlements = entitleToSave.filter(
            (en) =>
              DistributorUserIDRequiredList.some(
                (e) => e.distributorId === en.distributorId,
              ) ||
              TradingEntityDistributorPlatforms.some(
                (x) => x.distributorId === en.distributorId,
              ),
          );
          if (requiredEntitlements && requiredEntitlements.length > 0) {
            validateUserId(requiredEntitlements, entitleToSave);
          } else {
            saveEntitlements(entitleToSave);
          }
        }
      } catch (err) {}
    };

    const validateUserId = (requiredEntitlements, entitleToSave) => {
      try {
        let distributorIdCollection = [];
        let contactIdCollection = [];
        let uniqueRequiredEntitlements = [];
        requiredEntitlements.forEach((el, i) => {
          // Get only unique records
          if (
            !uniqueRequiredEntitlements.some(
              (x) =>
                x.contactId === el.contactId &&
                x.distributorId === el.distributorId,
            )
          ) {
            uniqueRequiredEntitlements.push(el);
          }

          distributorIdCollection.push(el.distributorId);
          contactIdCollection.push(el.contactId);
        });

        // create Unique ID's of distributor with userId required
        let uniqueDistributorIdCollection = [
          ...new Set(distributorIdCollection),
        ];
        let uniqueContactIdCollection = [...new Set(contactIdCollection)];

        // create query using above data
        distributorIdCollection = uniqueDistributorIdCollection
          .map((el) => el)
          .join(',');
        contactIdCollection = uniqueContactIdCollection
          .map((el) => `"${el}"`)
          .join(',');

        let queryParams = {
          Filter: `distributorId in(${distributorIdCollection}) and contactId in(${contactIdCollection})`,
          PageSize: maxPageSize,
          id: String(Date.now()),
        };
        setLoading(true);
        smartHttp
          .get(Endpoints.distributorUserApi, queryParams)
          .then(async (res) => {
            let distributorUser = res?.data?.results;

            uniqueRequiredEntitlements.map((re) => {
              let userDistributorData = distributorUser.find(
                (ud) =>
                  ud.distributorId === re.distributorId &&
                  ud.contactId === re.contactId,
              );
              if (
                TradingEntityDistributorPlatforms.some(
                  (x) => x.distributorId === re.distributorId,
                )
              ) {
                re.isValidated =
                  userDistributorData?.contact_Xref_Number &&
                  userDistributorData?.contact_Xref_Number?.trim();
                re.contact_Xref_Number =
                  userDistributorData?.contact_Xref_Number;
                if (!re.isValidated) return re;
              }

              if (
                DistributorUserIDRequiredList.some(
                  (x) => x.distributorId === re.distributorId,
                )
              ) {
                re.isValidated =
                  userDistributorData?.userId &&
                  userDistributorData?.userId?.trim();
                re.userId = userDistributorData?.userId;
              }
            });
            // Check if all Distributor Platforms has User ID
            if (uniqueRequiredEntitlements.every((x) => x.isValidated)) {
              saveEntitlements(entitleToSave);
            } else {
              let distributorUserIdModalData =
                uniqueRequiredEntitlements.filter((en) => !en.isValidated);
              // Set User ID mandatory data to show in modal
              setShowDistributorUserIdModal(distributorUserIdModalData);
            }
          })
          .catch((err) => {
            Notify({
              alert: true,
              type: 'error',
              title: GenericError.somethingWentWrong,
            });
          })
          .finally(() => setLoading(false));
      } catch (err) {}
    };

    const saveEntitlements = (postData, showAlert = true) => {
      let entitlementsForSave = postData.map((en) => {
        return {
          userEntitlementId: '-1',
          contactId: en.contactId,
          productComponentId: en.productComponentId,
          contractNumber: en.contractNumber,
          status: PendingActive, // FEATURE DISABLED (Release july-2026) - was: isBPMReferral ? Draft : PendingActive
        };
      });
      let entitlementModel = {
        userEntitlements: entitlementsForSave,
        userId: UserId,
      };
      setLoading(true);
      smartHttp
        .post(Endpoints.userentitlementsApi, entitlementModel)
        .then((res) => {
          if (res.data === 0) {
            smartHttp.clearCache(Endpoints.userentitlementsApi);
            Notify({
              alert: showAlert,
              type: 'success',
              title: Verbiage.entitlementsAdded,
              onConfirm: () => afterSaveClick(),
            });
          }
        })
        .catch((err) => {
          // FEATURE DISABLED (Release july-2026) - LME Governance Check (Story 9836589)
          // let errorMsg =
          //   err?.response?.data === -3
          //     ? Verbiage.lmeEntitlementsGovernanceFailed
          //     : GenericError.somethingWentWrong;
          let errorMsg = GenericError.somethingWentWrong;
          Notify({
            alert: true,
            type: 'error',
            title: errorMsg,
          });
        })
        .finally(() => setLoading(false));
    };

    const navigate = useNavigate();
    const afterSaveClick = () => {
      navigate(UrlPath.entitlements);
    };

    const handleEntitlementSave = () => {
      saveEntitlements(packageDistributorData);
      setShowDistributorUserIdModal([]);
    };

    const handleClose = () => {
      setShowDistributorUserIdModal([]);
    };

    let isGridDataAvailable =
      packageDistributorData &&
      packageDistributorData.length > 0 &&
      selectedUsers.length !== 0;
    return (
      <>
        {showDistributorUserIdModal.length > 0 && (
          <DistributorUserIdModal
            filteredDistributorUserIdData={showDistributorUserIdModal}
            handleEntitlementSave={handleEntitlementSave}
            handleClose={handleClose}
          />
        )}
        {loading && <Loader type="scaleLoader" cssClass={loaderStyle} />}

        <>
          <Container fluid>
            <Row>
              <Col>
                <div className="add-entitlements left-0 mt-2">
                  {/* New Entitlement */}
                </div>
              </Col>
            </Row>
            <Row>
              <Col>
                <UsersDropdown
                  contracts={contracts}
                  callBackUsers={callBackUsers}
                  isGroupFilterApply={true}
                />
              </Col>
              <Col>
                <PackagesDropdown
                  DataSet={filteredPackages}
                  callBackPackages={callBackPackages}
                />
              </Col>
              <Col>
                <DistributorsDropdown
                  DataSet={filteredDistributors}
                  callBackDistributors={callBackDistributors}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <div className="ag-grid-add-entitlement-table">
                  <div
                    className={
                      isGridDataAvailable ? 'd-none' : 'd-block starter-text'
                    }
                  >
                    <h4>Select any item</h4>
                    <p>
                      Select Users, Packages and Delivery Platform to start the
                      Entitlement Provision Process.
                    </p>
                  </div>
                  <div
                    className={
                      isGridDataAvailable
                        ? 'd-block ag-grid-add-entitlement-table'
                        : 'd-none'
                    }
                    ref={gridContainerRef}
                  >
                    <AgGridComponent
                      autoGroupColumnDef={autoGroupColumnDef}
                      config={defaultConfig}
                      defaultColumnDef={defaultColdef}
                      data={packageDistributorData}
                      columns={columns}
                      onGridReady={onGridReadyHandler}
                    />
                    <Col className="float-end mt-2 mb-2">
                      <button
                        className="btn btn-dark btn-opacity"
                        onClick={onSaveHandler}
                        disabled={packageDistributorData?.every(
                          (x) => x.isEntitlementExists,
                        )}
                      >
                        SAVE ENTITLEMENTS
                      </button>
                    </Col>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </>
      </>
    );
  },
  (op, np) => op?.contracts === np?.contracts,
);

export default AddEntitlements;
